import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useUserCard } from '../hooks/useUserCard';
import { useSubscriptionLimits } from '../hooks/useSubscriptionLimits';
import UpgradeModal from '../components/UpgradeModal';
import AddMembersModal from '../components/AddMembersModal';
import { useToast } from '../hooks/use-toast';
import { teamAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Users, UserPlus, Mail, Briefcase, Phone, Trash2, Edit, Crown, CheckCircle, XCircle, Clock, Link2, Send, Copy, UserCheck, Eye, Settings, Shield, Lock, Unlock, DollarSign, FileText, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const TeamSettings = () => {
  const { user } = useAuth();
  const { userCard, loading: cardLoading } = useUserCard();
  const { limits, loading: limitsLoading, checkLimit, isSubscribed, tier } = useSubscriptionLimits();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showCreateTeamDialog, setShowCreateTeamDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showInviteLinkDialog, setShowInviteLinkDialog] = useState(false);
  const [showDeleteInviteDialog, setShowDeleteInviteDialog] = useState(false);
  const [showAcceptInviteDialog, setShowAcceptInviteDialog] = useState(false);
  const [showDeleteTeamDialog, setShowDeleteTeamDialog] = useState(false);
  const [showMemberSettingsDialog, setShowMemberSettingsDialog] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [memberToConfigureSettings, setMemberToConfigureSettings] = useState(null);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [invitationLink, setInvitationLink] = useState('');
  const [invitationDetails, setInvitationDetails] = useState(null);
  const [processingInvite, setProcessingInvite] = useState(false);
  
  // Form states
  const [teamName, setTeamName] = useState('');
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    designation: '',
    phone: '',
    pipeline_ids: []  // Multi-select pipeline access
  });
  
  // Pipeline states for invite
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipelines, setSelectedPipelines] = useState([]);
  
  // Member-specific settings states
  const [memberDealVisibility, setMemberDealVisibility] = useState('all');
  const [memberAmountVisibility, setMemberAmountVisibility] = useState('all');
  const [memberCardEditPermission, setMemberCardEditPermission] = useState('all');
  const [memberCanManagePipelines, setMemberCanManagePipelines] = useState(false);
  const [memberCopyContactsToOwner, setMemberCopyContactsToOwner] = useState(true);

  // Pipeline access edit states
  const [showPipelineAccessDialog, setShowPipelineAccessDialog] = useState(false);
  const [memberToEditPipelines, setMemberToEditPipelines] = useState(null);
  const [memberPipelineAccess, setMemberPipelineAccess] = useState([]);

  // Team profile states
  const [profileForm, setProfileForm] = useState({
    owner_name: '',
    owner_phone: '',
    company_name: '',
    currency_symbol: '₹'
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  useEffect(() => {
    loadTeam();
    loadPipelines();
    checkInvitationToken();
  }, []);

  // Check for pending invitation when user logs in
  useEffect(() => {
    if (user && !loading) {
      checkPendingInvitation();
    }
  }, [user, loading]);

  const loadTeam = async () => {
    try {
      setLoading(true);
      const teamData = await teamAPI.getMyTeam();
      setTeam(teamData);
      
      // Populate profile form if team exists
      if (teamData && teamData.profile) {
        setProfileForm({
          owner_name: teamData.profile.owner_name || '',
          owner_phone: teamData.profile.owner_phone || '',
          company_name: teamData.profile.company_name || '',
          currency_symbol: teamData.profile.currency_symbol || '₹'
        });
      }
    } catch (error) {
      console.error('Error loading team:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPipelines = async () => {
    try {
      const { crmAPI } = await import('../services/api');
      const pipelinesData = await crmAPI.getPipelines();
      setPipelines(pipelinesData);
    } catch (error) {
      console.error('Error loading pipelines:', error);
    }
  };

  const checkInvitationToken = async () => {
    // Check if there's an invitation token in the URL
    const params = new URLSearchParams(location.search);
    const invitationToken = params.get('invitation');
    
    if (invitationToken) {
      // Check if user is logged in
      if (!user) {
        // Store invitation token in sessionStorage to persist through login
        sessionStorage.setItem('pendingInvitation', invitationToken);
        toast({
          title: "Login Required",
          description: "Please login to accept this team invitation",
        });
        // Redirect to home/login page
        navigate('/', { replace: true });
        return;
      }

      try {
        // Fetch pending invitations to find this one
        const invitations = await teamAPI.getPendingInvitations();
        const invitation = invitations.find(inv => inv.invitation_token === invitationToken);
        
        if (invitation) {
          setInvitationDetails(invitation);
          setShowAcceptInviteDialog(true);
          // Remove invitation param from URL
          navigate('/team-settings', { replace: true });
        } else {
          toast({
            title: "Invitation Not Found",
            description: "This invitation may have expired or been cancelled.",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Error loading invitation:', error);
        toast({
          title: "Error",
          description: "Failed to load invitation details",
          variant: "destructive"
        });
      }
    }
  };

  // Check for pending invitation after login
  const checkPendingInvitation = async () => {
    const pendingToken = sessionStorage.getItem('pendingInvitation');
    
    if (pendingToken && user) {
      // Clear from storage
      sessionStorage.removeItem('pendingInvitation');
      
      try {
        // Fetch pending invitations to find this one
        const invitations = await teamAPI.getPendingInvitations();
        const invitation = invitations.find(inv => inv.invitation_token === pendingToken);
        
        if (invitation) {
          setInvitationDetails(invitation);
          setShowAcceptInviteDialog(true);
        } else {
          toast({
            title: "Invitation Not Found",
            description: "This invitation may have expired or been cancelled.",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Error loading pending invitation:', error);
        toast({
          title: "Error",
          description: "Failed to load invitation details",
          variant: "destructive"
        });
      }
    }
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a team name",
        variant: "destructive"
      });
      return;
    }

    try {
      await teamAPI.createTeam(teamName);
      toast({
        title: "Success",
        description: "Team created successfully!"
      });
      setShowCreateTeamDialog(false);
      setTeamName('');
      await loadTeam();
    } catch (error) {
      console.error('Error creating team:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create team",
        variant: "destructive"
      });
    }
  };

  const handleOpenInviteDialog = () => {
    // Staff users cannot invite members
    if (team && team.my_role === 'staff') {
      toast({
        title: "Access Denied",
        description: "Only the team owner can invite new members. Please contact your team owner.",
        variant: "destructive"
      });
      return;
    }

    // Check if member limit is reached (including pending invitations)
    const activeMemberCount = team.members?.filter(m => m.status !== 'removed').length || 0;
    const pendingInvitationsCount = team.pending_invitations?.length || 0;
    const totalUsage = activeMemberCount + pendingInvitationsCount;
    const memberLimit = limits?.team_members || 1;
    
    console.log(`📊 Member limit check: ${totalUsage}/${memberLimit} (active: ${activeMemberCount}, pending: ${pendingInvitationsCount})`);
    
    if (totalUsage >= memberLimit) {
      toast({
        title: "Member Limit Reached",
        description: "Purchase additional member slots to add more team members.",
        variant: "destructive"
      });
      setShowAddMembersModal(true);
      return;
    }
    
    setShowInviteDialog(true);
  };

  const handleInviteMember = async () => {
    if (!inviteForm.email || !inviteForm.name || !inviteForm.designation) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    if (selectedPipelines.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one pipeline",
        variant: "destructive"
      });
      return;
    }

    try {
      const inviteData = {
        ...inviteForm,
        pipeline_ids: selectedPipelines
      };
      await teamAPI.inviteMember(team.team_id, inviteData);
      toast({
        title: "Success",
        description: `Invitation sent to ${inviteForm.email}`
      });
      setInviteForm({ email: '', name: '', designation: '', phone: '', pipeline_ids: [] });
      setSelectedPipelines([]);
      setShowInviteDialog(false);
      await loadTeam();
    } catch (error) {
      console.error('Error inviting member:', error);
      
      // Check if it's a member limit error (402)
      if (error.response?.status === 402) {
        setShowInviteDialog(false);
        setShowUpgradeModal(true);
        toast({
          title: "Member Limit Reached",
          description: "Created all the staff members allowed. Upgrade to add more members.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: error.response?.data?.detail || "Failed to send invitation",
          variant: "destructive"
        });
      }
    }
  };

  const handlePurchaseMembers = async (memberPackage) => {
    console.log('📦 Purchasing member package:', memberPackage);
    setShowAddMembersModal(false);
    
    const isYearly = tier === 'business_yearly' || tier === 'pro_yearly';
    
    // Open payment modal with member addon details
    const planDetails = {
      name: `${memberPackage.label} - ${isYearly ? 'Yearly' : 'Monthly'}`,
      price: memberPackage.price,
      memberCount: limits?.team_members + memberPackage.count,
      isAddon: true,
      addonType: 'members',
      additionalMembers: memberPackage.count,
      billingCycle: isYearly ? 'yearly' : 'monthly'
    };
    
    // Trigger payment modal
    window.dispatchEvent(new CustomEvent('showPaymentModal', { detail: planDetails }));
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    try {
      console.log('🗑️ Removing member:', {
        team_id: team.team_id,
        user_id: memberToRemove.user_id,
        name: memberToRemove.name
      });
      
      // Call remove API
      const response = await teamAPI.removeMember(team.team_id, memberToRemove.user_id);
      console.log('✅ Backend response:', response);
      
      // Close dialog first
      setShowRemoveDialog(false);
      
      // Reload team data
      console.log('🔄 Reloading team data...');
      await loadTeam();
      console.log('✅ Team data reloaded');
      
      // Show success toast after reload
      toast({
        title: "Success",
        description: `${memberToRemove.name} has been removed from the team`
      });
      
      setMemberToRemove(null);
    } catch (error) {
      console.error('❌ Error removing member:', error);
      console.error('Error details:', error.response?.data);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to remove member",
        variant: "destructive"
      });
      setShowRemoveDialog(false);
      setMemberToRemove(null);
    }
  };

  const openRemoveDialog = (member) => {
    setMemberToRemove(member);
    setShowRemoveDialog(true);
  };

  const handleGetInvitationLink = async (invitation) => {
    try {
      const data = await teamAPI.getInvitationLink(team.team_id, invitation.invitation_token);
      setInvitationLink(data.invitation_link);
      setSelectedInvitation(invitation);
      setShowInviteLinkDialog(true);
    } catch (error) {
      console.error('Error getting invitation link:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to get invitation link",
        variant: "destructive"
      });
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(invitationLink);
    toast({
      title: "Copied!",
      description: "Invitation link copied to clipboard"
    });
  };

  const handleResendInvitation = async (invitation) => {
    try {
      await teamAPI.resendInvitation(team.team_id, invitation.invitation_token);
      toast({
        title: "Success",
        description: `Invitation resent to ${invitation.email}`
      });
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to resend invitation",
        variant: "destructive"
      });
    }
  };

  const handleDeleteInvitation = async () => {
    if (!selectedInvitation) return;

    try {
      await teamAPI.deleteInvitation(team.team_id, selectedInvitation.invitation_token);
      toast({
        title: "Success",
        description: "Invitation deleted successfully"
      });
      setShowDeleteInviteDialog(false);
      setSelectedInvitation(null);
      await loadTeam();
    } catch (error) {
      console.error('Error deleting invitation:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete invitation",
        variant: "destructive"
      });
    }
  };

  const openDeleteInviteDialog = (invitation) => {
    setSelectedInvitation(invitation);
    setShowDeleteInviteDialog(true);
  };

  const handleDeleteTeam = async () => {
    try {
      await teamAPI.deleteTeam(team.team_id);
      toast({
        title: "Success",
        description: "Team deleted successfully. You can now join another team or create a new one."
      });
      setShowDeleteTeamDialog(false);
      navigate('/team-settings');
      // Reload to show create team option
      window.location.reload();
    } catch (error) {
      console.error('Error deleting team:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete team",
        variant: "destructive"
      });
    }
  };

  const openMemberSettingsDialog = (member) => {
    setMemberToConfigureSettings(member);
    setMemberDealVisibility(member.settings?.deal_visibility || 'assigned_only');
    setMemberAmountVisibility(member.settings?.amount_visibility || 'assigned_only');
    setMemberCardEditPermission(member.settings?.card_edit_permission || 'assigned_only');
    setMemberCanManagePipelines(member.settings?.can_manage_pipelines || false);
    setMemberCopyContactsToOwner(member.settings?.copy_contacts_to_owner ?? true);  // Default to true
    setShowMemberSettingsDialog(true);
  };

  const handleSaveMemberSettings = async () => {
    if (!memberToConfigureSettings) return;
    
    try {
      console.log('💾 Saving member settings:', {
        team_id: team.team_id,
        user_id: memberToConfigureSettings.user_id,
        deal_visibility: memberDealVisibility,
        amount_visibility: memberAmountVisibility,
        card_edit_permission: memberCardEditPermission,
        can_manage_pipelines: memberCanManagePipelines,
        copy_contacts_to_owner: memberCopyContactsToOwner
      });
      
      await teamAPI.updateMemberSettings(
        team.team_id, 
        memberToConfigureSettings.user_id, 
        memberDealVisibility, 
        memberAmountVisibility,
        memberCardEditPermission,
        memberCanManagePipelines,
        memberCopyContactsToOwner
      );
      
      console.log('✅ Settings saved successfully');
      
      toast({
        title: "Success",
        description: `Settings updated for ${memberToConfigureSettings.name}`
      });
      setShowMemberSettingsDialog(false);
      setMemberToConfigureSettings(null);
      await loadTeam();
    } catch (error) {
      console.error('❌ Error updating member settings:', error);
      console.error('Error details:', error.response?.data);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update member settings",
        variant: "destructive"
      });
    }
  };


  const handleAcceptInviteFromDialog = async () => {
    if (!invitationDetails) return;

    setProcessingInvite(true);
    try {
      await teamAPI.acceptInvitation(invitationDetails._id || invitationDetails.invitation_id);
      toast({
        title: "Success",
        description: `You've joined ${invitationDetails.team_name}!`
      });
      setShowAcceptInviteDialog(false);
      // Reload page to show new team
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to accept invitation",
        variant: "destructive"
      });
    } finally {
      setProcessingInvite(false);
    }
  };

  const handleRejectInviteFromDialog = async () => {
    if (!invitationDetails) return;

    setProcessingInvite(true);
    try {
      await teamAPI.rejectInvitation(invitationDetails._id || invitationDetails.invitation_id);
      toast({
        title: "Invitation Declined",
        description: "You've declined the team invitation"
      });
      setShowAcceptInviteDialog(false);
      setInvitationDetails(null);
    } catch (error) {
      console.error('Error rejecting invitation:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to reject invitation",
        variant: "destructive"
      });
    } finally {
      setProcessingInvite(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await teamAPI.updateTeamProfile(team.team_id, profileForm);
      toast({
        title: "Success",
        description: "Team profile updated successfully"
      });
      setIsEditingProfile(false);
      await loadTeam();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update profile",
        variant: "destructive"
      });
    }
  };

  const handleCancelEditProfile = () => {
    // Reset to original values
    if (team && team.profile) {
      setProfileForm({
        owner_name: team.profile.owner_name || '',
        owner_phone: team.profile.owner_phone || '',
        company_name: team.profile.company_name || '',
        currency_symbol: team.profile.currency_symbol || '₹'
      });
    }
    setIsEditingProfile(false);
  };

  const openEditPipelineAccessDialog = (member) => {
    setMemberToEditPipelines(member);
    setMemberPipelineAccess(member.pipeline_ids || []);
    setShowPipelineAccessDialog(true);
  };

  const handleUpdatePipelineAccess = async () => {
    if (memberPipelineAccess.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one pipeline',
        variant: 'destructive'
      });
      return;
    }

    try {
      await teamAPI.updateMemberPipelineAccess(
        team.team_id,
        memberToEditPipelines.user_id,
        memberPipelineAccess
      );
      toast({
        title: 'Success',
        description: 'Pipeline access updated successfully'
      });
      setShowPipelineAccessDialog(false);
      await loadTeam();
    } catch (error) {
      console.error('Error updating pipeline access:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update pipeline access',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <Layout userCard={userCard} loading={cardLoading}>
        <div className="min-h-screen bg-gray-50 py-4 sm:py-6 px-3 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // No team - show create team option
  if (!team) {
    return (
      <Layout userCard={userCard} loading={cardLoading}>
        <div className="min-h-screen bg-gray-50 py-4 sm:py-6 px-3 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Card className="shadow-lg">
              <CardHeader className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
                <CardTitle className="text-xl sm:text-2xl">Create Your Team</CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Build your sales team and manage your CRM pipeline together
                  {tier !== 'business' && (
                    <span className="block mt-2 text-xs font-semibold text-amber-600">
                      👑 Business Plan Feature
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-sm sm:text-base text-gray-600 mb-6">
                  Create a team to invite staff members, share your CRM pipeline, and collaborate on leads.
                </p>
                <Button
                  onClick={() => {
                    // Check if user is on Business plan
                    if (tier !== 'business') {
                      setShowUpgradeModal(true);
                      return;
                    }
                    setShowCreateTeamDialog(true);
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  size="lg"
                >
                  <Users className="w-5 h-5 mr-2" />
                  <span>Create Team</span>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Create Team Dialog */}
        <Dialog open={showCreateTeamDialog} onOpenChange={setShowCreateTeamDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Your Team</DialogTitle>
              <DialogDescription>
                Choose a name for your team. You can invite members after creating the team.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Team Name</label>
                <Input
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="e.g., Sales Team, Marketing Team"
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreateTeamDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateTeam}>Create Team</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Upgrade Modal for Non-Business Users */}
        <UpgradeModal
          open={showUpgradeModal}
          onOpenChange={(open) => {
            setShowUpgradeModal(open);
            if (!open && tier !== 'business') {
              // If they close modal and still not on business plan, redirect to pricing
              navigate('/pricing');
            }
          }}
          limitType="teams"
          currentUsage={0}
          maxLimit={0}
        />
      </Layout>
    );
  }

  const isOwner = team.my_role === 'owner';

  return (
    <Layout userCard={userCard} loading={cardLoading}>
      <div className="min-h-screen bg-gray-50 py-4 sm:py-6 px-3 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Users className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-amber-600 flex-shrink-0" />
                <span className="truncate">{team.team_name}</span>
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1 hidden sm:block">
                {isOwner ? 'Manage your team members and settings' : 'View your team members'}
              </p>
            </div>
            {isOwner && (
              <Button
                onClick={handleOpenInviteDialog}
                className="bg-amber-600 hover:bg-amber-700 text-white ml-2 flex-shrink-0"
                size="sm"
              >
                <UserPlus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Invite Member</span>
              </Button>
            )}
          </div>

          {/* Profile Settings */}
          <Card className="border-2 border-amber-200">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                    <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                    Team Profile
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {isOwner ? 'Manage team information and settings' : 'View team information'}
                  </CardDescription>
                </div>
                {isOwner && !isEditingProfile && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditingProfile(true)}
                    className="text-amber-600 hover:bg-amber-50"
                    title="Edit Profile"
                  >
                    <Edit className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Edit</span>
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Owner Name */}
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-gray-500" />
                    Owner Name
                  </label>
                  {isOwner && isEditingProfile ? (
                    <Input
                      value={profileForm.owner_name}
                      onChange={(e) => setProfileForm({ ...profileForm, owner_name: e.target.value })}
                      placeholder="Enter name"
                      className="mt-1"
                    />
                  ) : (
                    <div className="mt-1 p-2 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-900">{profileForm.owner_name || '-'}</p>
                    </div>
                  )}
                </div>

                {/* Phone Number */}
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-500" />
                    Phone Number
                  </label>
                  {isOwner && isEditingProfile ? (
                    <Input
                      value={profileForm.owner_phone}
                      onChange={(e) => setProfileForm({ ...profileForm, owner_phone: e.target.value })}
                      placeholder="Enter phone number"
                      className="mt-1"
                    />
                  ) : (
                    <div className="mt-1 p-2 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-900">{profileForm.owner_phone || '-'}</p>
                    </div>
                  )}
                </div>

                {/* Company Name */}
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-gray-500" />
                    Company Name
                  </label>
                  {isOwner && isEditingProfile ? (
                    <Input
                      value={profileForm.company_name}
                      onChange={(e) => setProfileForm({ ...profileForm, company_name: e.target.value })}
                      placeholder="Enter company name"
                      className="mt-1"
                    />
                  ) : (
                    <div className="mt-1 p-2 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-900">{profileForm.company_name || '-'}</p>
                    </div>
                  )}
                </div>

                {/* Currency Symbol */}
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-500" />
                    Currency Symbol
                  </label>
                  {isOwner && isEditingProfile ? (
                    <Input
                      value={profileForm.currency_symbol}
                      onChange={(e) => setProfileForm({ ...profileForm, currency_symbol: e.target.value })}
                      placeholder="e.g., ₹, $, €"
                      className="mt-1"
                      maxLength={3}
                    />
                  ) : (
                    <div className="mt-1 p-2 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-900">{profileForm.currency_symbol || '-'}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons (shown when editing) */}
              {isOwner && isEditingProfile && (
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-6 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleCancelEditProfile}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpdateProfile}
                    className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto"
                  >
                    <CheckCircle className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Save Changes</span>
                    <span className="sm:hidden">Save</span>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Team Members */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between flex-wrap gap-2 text-lg sm:text-xl">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                  <span className="truncate">Team Members</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-normal">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    ((team.members?.filter(m => m.status !== 'removed').length || 0) + (team.pending_invitations?.length || 0)) >= (limits?.team_members || 1)
                      ? 'bg-red-100 text-red-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {(team.members?.filter(m => m.status !== 'removed').length || 0) + (team.pending_invitations?.length || 0)} / {limits?.team_members || 1} Used
                  </span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {team.members?.filter(member => member.status !== 'removed').map((member) => (
                  <div
                    key={member.user_id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border rounded-lg hover:bg-gray-50 transition-colors gap-3"
                  >
                    <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0">
                        {member.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-sm sm:text-base text-gray-900 truncate">{member.name}</h3>
                          {member.role === 'owner' && (
                            <span className="inline-flex items-center gap-1 bg-amber-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0">
                              <Crown className="w-3 h-3" />
                              Owner
                            </span>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600">{member.designation}</p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1">
                          <span className="text-xs text-gray-500 flex items-center gap-1 truncate">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{member.email}</span>
                          </span>
                          {member.phone && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Phone className="w-3 h-3 flex-shrink-0" />
                              {member.phone}
                            </span>
                          )}
                        </div>
                        {/* Pipeline Access Badges */}
                        {member.role === 'staff' && member.pipeline_ids && member.pipeline_ids.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            <span className="text-xs text-gray-500 font-medium mr-1">Pipelines:</span>
                            {member.pipeline_ids.slice(0, 3).map((pipelineId) => {
                              const pipeline = pipelines.find(p => p.id === pipelineId);
                              return pipeline ? (
                                <span
                                  key={pipelineId}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full"
                                >
                                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                                  {pipeline.name}
                                </span>
                              ) : null;
                            })}
                            {member.pipeline_ids.length > 3 && (
                              <span className="text-xs text-gray-500">+{member.pipeline_ids.length - 3} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {isOwner && member.role !== 'owner' && (
                      <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:bg-blue-50 p-2"
                          onClick={() => openEditPipelineAccessDialog(member)}
                          title="Edit pipeline access"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-amber-600 hover:bg-amber-50 p-2"
                          onClick={() => openMemberSettingsDialog(member)}
                          title="Configure member settings"
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50 p-2"
                          onClick={() => openRemoveDialog(member)}
                          title="Remove member"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Pending Invitations (Owner Only) */}
          {isOwner && team.pending_invitations && team.pending_invitations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                  <span className="truncate">Pending Invitations ({team.pending_invitations.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {team.pending_invitations.map((invitation) => (
                    <div
                      key={invitation.invitation_token}
                      className="flex flex-col sm:flex-row sm:items-start justify-between p-3 sm:p-4 border border-orange-200 bg-orange-50 rounded-lg gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base text-gray-900 truncate">{invitation.name}</h3>
                        <p className="text-xs sm:text-sm text-gray-600">{invitation.designation}</p>
                        <p className="text-xs text-gray-500 mt-1 truncate">{invitation.email}</p>
                        <p className="text-xs text-orange-600 mt-1">
                          Expires: {new Date(invitation.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 ml-2 sm:ml-4 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-amber-600 hover:bg-amber-50"
                          onClick={() => handleGetInvitationLink(invitation)}
                          title="Get invitation link"
                        >
                          <Link2 className="w-3 h-3 sm:mr-1" />
                          <span className="hidden sm:inline">Get Link</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 hover:bg-blue-50"
                          onClick={() => handleResendInvitation(invitation)}
                          title="Resend invitation"
                        >
                          <Send className="w-3 h-3 sm:mr-1" />
                          <span className="hidden sm:inline">Resend</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => openDeleteInviteDialog(invitation)}
                          title="Delete invitation"
                        >
                          <Trash2 className="w-3 h-3 sm:mr-1" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Danger Zone (Owner Only) */}
          {isOwner && (
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-600 flex items-center gap-2 text-lg sm:text-xl">
                  <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                  Danger Zone
                </CardTitle>
                <CardDescription className="text-sm">
                  Permanently delete this team. This action cannot be undone.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  className="w-full sm:w-auto text-red-600 hover:bg-red-50 border-red-300"
                  onClick={() => setShowDeleteTeamDialog(true)}
                >
                  <Trash2 className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Delete Team</span>
                  <span className="sm:hidden">Delete</span>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Invite Member Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to add a new member to your team
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium flex items-center gap-1">
                Email <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="member@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium flex items-center gap-1">
                Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="John Doe"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium flex items-center gap-1">
                Designation <span className="text-red-500">*</span>
              </label>
              <Input
                value={inviteForm.designation}
                onChange={(e) => setInviteForm({ ...inviteForm, designation: e.target.value })}
                placeholder="e.g., Sales Executive"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Phone (Optional)</label>
              <Input
                type="tel"
                value={inviteForm.phone}
                onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                placeholder="+1234567890"
                className="mt-1"
              />
            </div>

            {/* Pipeline Access Selection */}
            <div>
              <label className="text-sm font-medium flex items-center gap-1">
                Pipeline Access <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 mb-2">
                Select which pipelines this member can access
              </p>
              <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                {pipelines.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No pipelines available. Create a pipeline first.
                  </p>
                ) : (
                  pipelines.map((pipeline) => (
                    <label
                      key={pipeline.id}
                      className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedPipelines.includes(pipeline.id)
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-amber-300 bg-white'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPipelines.includes(pipeline.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPipelines([...selectedPipelines, pipeline.id]);
                          } else {
                            setSelectedPipelines(selectedPipelines.filter(id => id !== pipeline.id));
                          }
                        }}
                        className="mt-1 w-4 h-4 text-amber-600"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full bg-amber-500"
                          ></div>
                          <span className="font-semibold text-sm text-gray-900">{pipeline.name}</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5">{pipeline.description}</p>
                      </div>
                    </label>
                  ))
                )}
              </div>
              {selectedPipelines.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {selectedPipelines.map((pipelineId) => {
                    const pipeline = pipelines.find(p => p.id === pipelineId);
                    return pipeline ? (
                      <span
                        key={pipelineId}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full"
                      >
                        {pipeline.name}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowInviteDialog(false);
                setSelectedPipelines([]);
              }}>
                Cancel
              </Button>
              <Button onClick={handleInviteMember}>Send Invitation</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Member Dialog */}
      <Dialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {memberToRemove?.name} from the team?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                This action will:
              </p>
              <ul className="list-disc list-inside text-sm text-red-700 mt-2 space-y-1">
                <li>Remove their access to the team CRM</li>
                <li>Unassign all their leads</li>
                <li>This action cannot be undone</li>
              </ul>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRemoveDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleRemoveMember}>
                Remove Member
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invitation Link Dialog */}
      <Dialog open={showInviteLinkDialog} onOpenChange={setShowInviteLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invitation Link</DialogTitle>
            <DialogDescription>
              Share this link with {selectedInvitation?.name} to join the team
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedInvitation && (
              <div className="bg-gray-50 border rounded-lg p-3">
                <p className="text-sm font-semibold text-gray-900">{selectedInvitation.name}</p>
                <p className="text-xs text-gray-600">{selectedInvitation.email}</p>
                <p className="text-xs text-gray-600">{selectedInvitation.designation}</p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium">Invitation Link</label>
              <div className="mt-2 flex gap-2">
                <Input
                  value={invitationLink}
                  readOnly
                  className="flex-1"
                />
                <Button onClick={handleCopyLink} className="flex-shrink-0">
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                💡 Share this link via email, chat, or any messaging app. The recipient can click the link to accept the invitation.
              </p>
            </div>

            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setShowInviteLinkDialog(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Invitation Dialog */}
      <Dialog open={showDeleteInviteDialog} onOpenChange={setShowDeleteInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Invitation</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this invitation?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedInvitation && (
              <div className="bg-gray-50 border rounded-lg p-3">
                <p className="text-sm font-semibold text-gray-900">{selectedInvitation.name}</p>
                <p className="text-xs text-gray-600">{selectedInvitation.email}</p>
                <p className="text-xs text-gray-600">{selectedInvitation.designation}</p>
              </div>
            )}
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                ⚠️ This action cannot be undone. The invitation link will no longer work, and {selectedInvitation?.name} won't be able to join the team using this invitation.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDeleteInviteDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteInvitation}>
                Delete Invitation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Member Settings Dialog - Minimized Design */}
      <Dialog open={showMemberSettingsDialog} onOpenChange={setShowMemberSettingsDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Privacy Settings for {memberToConfigureSettings?.name}
            </DialogTitle>
            <DialogDescription className="text-sm">
              Configure what this member can see and do
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 overflow-y-auto flex-1 px-1">
            {/* Deal Card Visibility */}
            <div>
              <label className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Eye className="w-4 h-4 text-amber-600" />
                Who can see Deal cards
              </label>
              <div className="space-y-2 mt-2">
                <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    memberDealVisibility === 'all' 
                      ? 'border-amber-500 bg-amber-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input
                    type="radio"
                    name="memberDealVisibility"
                    value="all"
                    checked={memberDealVisibility === 'all'}
                    onChange={(e) => setMemberDealVisibility(e.target.value)}
                    className="w-4 h-4 text-amber-600"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Unlock className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-sm">All team deals</span>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    memberDealVisibility === 'assigned_only' 
                      ? 'border-amber-500 bg-amber-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input
                    type="radio"
                    name="memberDealVisibility"
                    value="assigned_only"
                    checked={memberDealVisibility === 'assigned_only'}
                    onChange={(e) => setMemberDealVisibility(e.target.value)}
                    className="w-4 h-4 text-amber-600"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Lock className="w-4 h-4 text-orange-600" />
                    <span className="font-medium text-sm">Only assigned deals</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Amount Visibility */}
            <div>
              <label className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-600" />
                Who can see deal amounts
              </label>
              <div className="space-y-2 mt-2">
                <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    memberAmountVisibility === 'all' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input
                    type="radio"
                    name="memberAmountVisibility"
                    value="all"
                    checked={memberAmountVisibility === 'all'}
                    onChange={(e) => setMemberAmountVisibility(e.target.value)}
                    className="w-4 h-4 text-green-600"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Unlock className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-sm">All amounts</span>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    memberAmountVisibility === 'assigned_only' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input
                    type="radio"
                    name="memberAmountVisibility"
                    value="assigned_only"
                    checked={memberAmountVisibility === 'assigned_only'}
                    onChange={(e) => setMemberAmountVisibility(e.target.value)}
                    className="w-4 h-4 text-green-600"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Lock className="w-4 h-4 text-orange-600" />
                    <span className="font-medium text-sm">Only assigned amounts</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Card Edit Permission */}
            <div>
              <label className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                What cards can be opened
              </label>
              <div className="space-y-2 mt-2">
                <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    memberCardEditPermission === 'all' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input
                    type="radio"
                    name="memberCardEditPermission"
                    value="all"
                    checked={memberCardEditPermission === 'all'}
                    onChange={(e) => setMemberCardEditPermission(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Unlock className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-sm">All cards</span>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    memberCardEditPermission === 'assigned_only' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input
                    type="radio"
                    name="memberCardEditPermission"
                    value="assigned_only"
                    checked={memberCardEditPermission === 'assigned_only'}
                    onChange={(e) => setMemberCardEditPermission(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Lock className="w-4 h-4 text-orange-600" />
                    <span className="font-medium text-sm">Only assigned cards</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Pipeline Management Permission */}
            <div>
              <label className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Settings className="w-4 h-4 text-amber-600" />
                Pipeline Management
              </label>
              <div className="space-y-2 mt-2">
                <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    memberCanManagePipelines
                      ? 'border-amber-500 bg-amber-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input
                    type="checkbox"
                    checked={memberCanManagePipelines}
                    onChange={(e) => setMemberCanManagePipelines(e.target.checked)}
                    className="w-4 h-4 text-amber-600 rounded"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Unlock className="w-4 h-4 text-amber-600" />
                    <div>
                      <span className="font-medium text-sm block">Can Manage Pipelines</span>
                      <span className="text-xs text-gray-500">Allow this member to manage pipelines and update stages</span>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Contact Syncing Permission */}
            <div>
              <label className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4 text-green-600" />
                Contact Syncing
              </label>
              <div className="space-y-2 mt-2">
                <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    memberCopyContactsToOwner
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input
                    type="checkbox"
                    checked={memberCopyContactsToOwner}
                    onChange={(e) => setMemberCopyContactsToOwner(e.target.checked)}
                    className="w-4 h-4 text-green-600 rounded"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <UserPlus className="w-4 h-4 text-green-600" />
                    <div>
                      <span className="font-medium text-sm block">Copy Contacts to Owner Account</span>
                      <span className="text-xs text-gray-500">Automatically copy all new contacts (digital & scanned) to owner</span>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons - Fixed at bottom */}
          <div className="flex justify-end gap-3 pt-4 border-t flex-shrink-0 bg-white">
            <Button 
              variant="outline" 
              onClick={() => setShowMemberSettingsDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveMemberSettings}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Save Settings
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Delete Team Dialog */}
      <Dialog open={showDeleteTeamDialog} onOpenChange={setShowDeleteTeamDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete {team?.team_name}?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-800 mb-2">
                ⚠️ This action will permanently:
              </p>
              <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                <li>Delete the team and all team data</li>
                <li>Remove all team members from the team</li>
                <li>Cancel all pending invitations</li>
                <li>Unassign all leads from team members</li>
                <li>This action cannot be undone</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                💡 After deleting, you'll be able to create a new team or join another team as a staff member.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDeleteTeamDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteTeam}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Team Permanently
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Accept Invitation Dialog (from URL link) */}
      <Dialog open={showAcceptInviteDialog} onOpenChange={setShowAcceptInviteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mb-4">
              <UserCheck className="w-8 h-8 text-white" />
            </div>
            <DialogTitle className="text-center text-2xl">Team Invitation</DialogTitle>
            <DialogDescription className="text-center">
              You've been invited to join a team!
            </DialogDescription>
          </DialogHeader>
          
          {invitationDetails && (
            <div className="space-y-4">
              {/* Team Info Card */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900">{invitationDetails.team_name}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-semibold">{invitationDetails.owner_name}</span> invited you to join as
                    </p>
                    <div className="inline-flex items-center gap-2 mt-2 bg-white border border-amber-300 rounded-full px-3 py-1">
                      <Briefcase className="w-4 h-4 text-amber-600" />
                      <span className="font-semibold text-amber-900">{invitationDetails.designation}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-sm text-blue-900 mb-2">What you'll get:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Access to shared CRM pipeline</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Collaborate on leads with your team</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>View team analytics and reports</span>
                  </li>
                </ul>
              </div>

              {/* Expiration Notice */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-xs text-orange-800 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Invitation expires on {new Date(invitationDetails.expires_at).toLocaleDateString()}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 text-red-600 hover:bg-red-50 border-red-200"
                  onClick={handleRejectInviteFromDialog}
                  disabled={processingInvite}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Decline
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleAcceptInviteFromDialog}
                  disabled={processingInvite}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {processingInvite ? 'Accepting...' : 'Accept Invitation'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Pipeline Access Dialog */}
      <Dialog open={showPipelineAccessDialog} onOpenChange={setShowPipelineAccessDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Edit Pipeline Access - {memberToEditPipelines?.name}
            </DialogTitle>
            <DialogDescription>
              Select which pipelines this member can access
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-4 bg-gray-50">
              {pipelines.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">
                  No pipelines available
                </p>
              ) : (
                pipelines.map((pipeline) => (
                  <label
                    key={pipeline.id}
                    className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      memberPipelineAccess.includes(pipeline.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 bg-white'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={memberPipelineAccess.includes(pipeline.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setMemberPipelineAccess([...memberPipelineAccess, pipeline.id]);
                        } else {
                          setMemberPipelineAccess(memberPipelineAccess.filter(id => id !== pipeline.id));
                        }
                      }}
                      className="mt-1 w-4 h-4 text-blue-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                        <span className="font-semibold text-sm text-gray-900">{pipeline.name}</span>
                        {pipeline.is_active && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600">{pipeline.description}</p>
                    </div>
                  </label>
                ))
              )}
            </div>

            {/* Selected Pipelines Summary */}
            {memberPipelineAccess.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-semibold text-blue-900 mb-2">
                  Selected Pipelines ({memberPipelineAccess.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {memberPipelineAccess.map((pipelineId) => {
                    const pipeline = pipelines.find(p => p.id === pipelineId);
                    return pipeline ? (
                      <span
                        key={pipelineId}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full"
                      >
                        {pipeline.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowPipelineAccessDialog(false);
                setMemberPipelineAccess([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdatePipelineAccess}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Update Access
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Members Modal */}
      <AddMembersModal
        open={showAddMembersModal}
        onClose={() => setShowAddMembersModal(false)}
        onPurchase={handlePurchaseMembers}
        currentLimit={limits?.team_members || 1}
        isYearly={tier === 'business_yearly' || tier === 'pro_yearly'}
      />

    </Layout>
  );
};

export default TeamSettings;
